import React, { useCallback, useState } from "react";
import { Switch, Tooltip } from "@material-ui/core";
import clsx from "clsx";
import { Link, useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import CloseIcon from "@material-ui/icons/Close";
import Input from "../DesignSystem/input";
import Button from "../DesignSystem/button";
import DropdownCheckbox from "../DesignSystem/dropdown_checkbox";
import SearchIcon from "../../assets/images/search-icon.svg";
import ListIcon from "../../assets/images/list-icon.svg";
import ListIconSelected from "../../assets/images/list-icon-selected.svg";
import MapViewIcon from "../../assets/images/map-view.svg";
import MapViewSelectedIcon from "../../assets/images/map-view-selected.svg";
import { AuthRoutes } from "../../enums";
import { priceFilterDropdown } from "../../utils/appConstants";
import Dropdown from "../DesignSystem/dropdown";
import WarningInfoIcon from "../../assets/images/warn-toast-icon.svg";
import SelectNode from "../Dashboard/Canvas/AddNetworkResource/SelectNodeContent";

export interface AdminValidationsLayoutProps {
  title: string;
  table: React.ReactNode;
  isVerifiedSection?: boolean;
  isDeleteSection?: boolean;
  isGroupedSection?: boolean;
  isPortView?: boolean;
  isPriceFilter?: boolean;
  isSearchSection?: boolean;
  isSelectPortView?: boolean;
  isMarketplace?: boolean;
  isMapview?: boolean;
  ports?: boolean;
  deletePorts?: boolean;
  deleteBandwidth?: boolean;
  bandwidth?: boolean;
  verified?: boolean;
  verifyAllLoading?: boolean;
  deleteAllLoading?: boolean;
  groupLoading?: boolean;
  unGroupLoading?: boolean;
  requestVerificationLoading?: boolean;
  isNotToggleSwitch?: boolean;
  selected?: any[];
  priceFilterValue?: any;
  groupedSelected?: any[];
  unGroupedSelected?: any[];
  requestVerificationSelected?: any[];
  openNetworkElement?: any;
  onVerifiedChange?: (checked: boolean) => void;
  onPortViewChange?: (checked: boolean) => void;
  onSearchChanges: (searchText: string) => void;
  onVerifyAllClick?: () => void;
  onDeleteAllClick?: () => void;
  onGroupClick?: () => void;
  onUnGroupClick?: () => void;
  onRequestVerificationClick?: () => void;
  onPriceFilterChange?: (value: string) => void;
  dropdown?: any;
}

const AdminValidationsLayout: React.FC<AdminValidationsLayoutProps> = ({
  title,
  dropdown,
  table,
  deletePorts,
  openNetworkElement,
  requestVerificationSelected,
  requestVerificationLoading,
  deleteAllLoading,
  deleteBandwidth,
  isVerifiedSection,
  isDeleteSection,
  isNotToggleSwitch,
  isPriceFilter,
  priceFilterValue,
  isSearchSection,
  isSelectPortView,
  isGroupedSection,
  isPortView,
  isMarketplace,
  ports,
  bandwidth,
  verified,
  verifyAllLoading,
  groupLoading,
  unGroupLoading,
  selected,
  groupedSelected,
  onUnGroupClick,
  onGroupClick,
  unGroupedSelected,
  isMapview,
  onPriceFilterChange,
  onVerifiedChange,
  onPortViewChange,
  onSearchChanges,
  onDeleteAllClick,
  onVerifyAllClick,
  onRequestVerificationClick,
}) => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<string>("");
  const { pathname } = window.location;

  const handleDeleteAllBtnClick = useCallback(() => {
    return confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="custom-ui-alert">
            <CloseIcon
              className="close-icon-popup"
              onClick={() => {
                onClose();
              }}
            />
            <div>
              <img src={WarningInfoIcon} alt="Warning" />
              {deleteBandwidth ? <label>Do you want to delete selected links?</label> : <label>Do you want to delete selected ports?</label>}
            </div>
            <div />
            <div className="buttons">
              <Button
                onClick={() => {
                  onClose();
                }}
                node="No"
                variant="greyed"
                className="cancel"
              />
              <Button
                onClick={() => {
                  onDeleteAllClick && onDeleteAllClick();
                  onClose();
                }}
                node="Yes"
                variant="primary"
              />
            </div>
          </div>
        );
      },
      closeOnEscape: true,
      closeOnClickOutside: true,
    });
  }, [onDeleteAllClick]);

  const handleVerifiedChange = useCallback(
    (event) => {
      onVerifiedChange && onVerifiedChange(event.target.checked);
    },
    [onVerifiedChange]
  );

  const handlePortViewChange = useCallback(
    (event) => {
      onPortViewChange && onPortViewChange(event.target.checked);
    },
    [onPortViewChange]
  );

  const handlePriceFilter = useCallback(
    (value: any) => {
      onPriceFilterChange && onPriceFilterChange(value);
    },
    [onPriceFilterChange]
  );

  const handleSearchChange = useCallback(
    (event) => {
      setSearchText(event.target.value);

      if (event.target.value === "") {
        onSearchChanges("");
      }
    },
    [setSearchText, onSearchChanges]
  );

  const handleSearchClicked = useCallback(() => {
    onSearchChanges(searchText.trim());
  }, [searchText, onSearchChanges]);

  const handleKeypress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSearchClicked();
      }
    },
    [handleSearchClicked]
  );

  let rightActionSection;
  if (isVerifiedSection) {
    rightActionSection = (
      <div className="verify-all-btn-div">
        {bandwidth && verified && <label className="highlighted-text">(The verified ports are shown in highlighted) </label>}
        {!isNotToggleSwitch && (
          <div className="show-verified-div">
            <label>Show Verified</label>
            <Switch checked={verified} onChange={handleVerifiedChange} data-testid="switch" />
          </div>
        )}
        {/* {!isVerifiedSection && <div className="empty-space-no-verified" />} */}
        {/* {!verified && <Button variant="success" text={`Verify All (${selected && selected.length})`} className="verify-all-btn" disabled={!selected || selected.length <= 0} loading={verifyAllLoading} onClick={onVerifyAllClick} />} */}
        {/* {isVerifiedSection && verified && <div className="empty-space" />} */}
      </div>
    );
  } else if (isDeleteSection) {
    rightActionSection = (
      <div className="verify-all-btn-div">
        {!isDeleteSection && <div className="empty-space-no-verified" />}
        {!verified && (
          <Button
            variant="success"
            node={`Delete All (${selected && selected.length})`}
            className={clsx("verify-all-btn", selected && selected.length > 0 && "delete-all-btn")}
            disabled={selected && selected.length <= 0}
            loading={deleteAllLoading}
            onClick={handleDeleteAllBtnClick}
          />
        )}
        {isDeleteSection && verified && <div className="empty-space" />}
      </div>
    );
  } else if (isGroupedSection) {
    rightActionSection = (
      <div className="group-ungroup-div">
        <Button
          variant="grouped"
          node="Request Verification"
          className="verify-all-btn"
          disabled={
            (groupedSelected && groupedSelected.length <= 0 && unGroupedSelected && unGroupedSelected.length <= 0) ||
            (requestVerificationSelected &&
              requestVerificationSelected[0] &&
              groupedSelected &&
              requestVerificationSelected[0].length !== groupedSelected.length) ||
            (requestVerificationSelected &&
              requestVerificationSelected[1] &&
              unGroupedSelected &&
              requestVerificationSelected[1].length !== unGroupedSelected.length)
          }
          loading={requestVerificationLoading}
          onClick={onRequestVerificationClick}
        />
        <Button
          variant="grouped"
          node="Group"
          className="verify-all-btn"
          disabled={!groupedSelected || groupedSelected.length <= 0}
          loading={groupLoading}
          onClick={onGroupClick}
        />
        <Button
          variant="grouped"
          node="Ungroup"
          className="verify-all-btn"
          disabled={!unGroupedSelected || unGroupedSelected.length <= 0}
          loading={unGroupLoading}
          onClick={onUnGroupClick}
        />
        <Button variant="grouped" node="Add Port" className="verify-all-btn" onClick={() => navigate(AuthRoutes.addPort, { state: openNetworkElement })} />
      </div>
    );
  } else if (isPriceFilter) {
    const priceValueArray = [];
    if (priceFilterValue === priceFilterDropdown[0].value) {
      priceValueArray.push(priceFilterDropdown[1].text);
      priceValueArray.push(priceFilterDropdown[2].text);
    }
    if (priceFilterValue === priceFilterDropdown[1].value) {
      priceValueArray.push(priceFilterDropdown[1].text);
    }
    if (priceFilterValue === priceFilterDropdown[2].value) {
      priceValueArray.push(priceFilterDropdown[2].text);
    }

    rightActionSection = (
      <div className="price-filter-dropdown">
        {dropdown}

        <DropdownCheckbox
          width={200}
          title="Purchase Type"
          value={priceValueArray}
          options={priceFilterDropdown.map((item: any) => item.text).filter((item: any) => item !== priceFilterDropdown[0].text)}
          customDisplayMethod={() => {
            if (priceFilterValue === priceFilterDropdown[0].value) {
              return priceFilterDropdown[0].text;
            }
            if (priceFilterValue === priceFilterDropdown[1].value) {
              return priceFilterDropdown[1].text;
            }
            if (priceFilterValue === priceFilterDropdown[2].value) {
              return priceFilterDropdown[2].text;
            }
            return "";
          }}
          onChange={handlePriceFilter}
        />
      </div>
    );
  } else if (isPortView) {
    rightActionSection = (
      <div className="verify-all-btn-div">
        {/* <div className="show-verified-div">
          <label>Link View</label>
          <Switch checked={verified} onChange={handlePortViewChange} data-testid="switch" />
          <label>Port View</label>
        </div> */}
        {verified && (
          <div className="group-ungroup-div">
            <Button
              variant="grouped"
              node="Request Verification"
              className="verify-all-btn"
              disabled={
                (groupedSelected && groupedSelected.length <= 0 && unGroupedSelected && unGroupedSelected.length <= 0) ||
                (requestVerificationSelected &&
                  requestVerificationSelected[0] &&
                  groupedSelected &&
                  requestVerificationSelected[0].length !== groupedSelected.length) ||
                (requestVerificationSelected &&
                  requestVerificationSelected[1] &&
                  unGroupedSelected &&
                  requestVerificationSelected[1].length !== unGroupedSelected.length)
              }
              loading={requestVerificationLoading}
              onClick={onRequestVerificationClick}
            />
            <Button
              variant="grouped"
              node="Group"
              className="verify-all-btn"
              disabled={!groupedSelected || groupedSelected.length <= 0}
              loading={groupLoading}
              onClick={onGroupClick}
            />
            <Button
              variant="grouped"
              node="Ungroup"
              className="verify-all-btn"
              disabled={!unGroupedSelected || unGroupedSelected.length <= 0}
              loading={unGroupLoading}
              onClick={onUnGroupClick}
            />
          </div>
        )}
        {!verified && (
          <div className="group-ungroup-div">
            <Button
              variant="grouped"
              node="Add Link"
              className="verify-all-btn"
              onClick={() => navigate(AuthRoutes.addBandwidth, { state: openNetworkElement })}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="verification-root" data-testid="admin-verification">
      <div className="sticky-top">
        <div className="title-main">
          <div className="title-root">
            <h3>{title}</h3>
          </div>
          {!!(!isMarketplace && (ports || bandwidth)) && (
            <div className="btn-group-root">
              <div className="btn-group">
                <Link to={isMarketplace ? AuthRoutes.marketPlacePorts : AuthRoutes.portVerification} className="ports-link">
                  <Button
                    variant="primary"
                    className={clsx((pathname === AuthRoutes.portVerification || pathname === AuthRoutes.marketPlacePorts) && "active")}
                    node="Ports"
                  />
                </Link>
                <Link to={isMarketplace ? AuthRoutes.marketPlaceBandwidthMap : AuthRoutes.bandwidthVerification} className="bandwidth-link">
                  <Button
                    variant="primary"
                    className={clsx((pathname === AuthRoutes.bandwidthVerification || pathname === AuthRoutes.marketPlaceBandwidth) && "active")}
                    node="Links"
                  />
                </Link>
              </div>
            </div>
          )}
          {!!(deletePorts || deleteBandwidth) && (
            <div className="btn-group-root">
              <div className="btn-group">
                <Link to={AuthRoutes.deletePorts} className="ports-link">
                  <Button variant="primary" className={clsx(pathname === AuthRoutes.deletePorts && "active")} node="Ports" />
                </Link>
                <Link to={AuthRoutes.deleteBandwidthPorts} className="bandwidth-link">
                  <Button variant="primary" className={clsx(pathname === AuthRoutes.deleteBandwidthPorts && "active")} node="Links" />
                </Link>
              </div>
            </div>
          )}
          {!!(isMarketplace && (bandwidth || isMapview)) && (
            <>
              {isMapview && (
                <div className="btn-group-root">
                  <div className="btn-group" />
                </div>
              )}
              {/* <div className="map-list-div">
                  <Tooltip
                    title="Link View"
                    placement="bottom"
                    arrow
                    classes={{
                      tooltip: 'tooltip',
                    }}
                  >
                    <Link to={AuthRoutes.marketPlaceBandwidth} className="list-link"><img src={pathname === AuthRoutes.marketPlaceBandwidth ? ListIconSelected : ListIcon} alt="List" onClick={onGroupClick} /></Link>
                  </Tooltip>
                  <Tooltip
                    title="Map View"
                    placement="bottom"
                    arrow
                    classes={{
                      tooltip: 'tooltip',
                    }}
                  >
                    <Link to={AuthRoutes.marketPlaceBandwidthMap} className="map-link"><img src={pathname === AuthRoutes.marketPlaceBandwidthMap ? MapViewSelectedIcon : MapViewIcon} alt="Map" onClick={onUnGroupClick} /></Link>
                  </Tooltip>
                </div> */}
            </>
          )}
        </div>
        <div className="table-filters">
          {isSearchSection && (
            <div className="search-box">
              <div className="search-input-div">
                <Input
                  variant="input-pre-icon"
                  name="user-search"
                  placeholder="Search"
                  icon={SearchIcon}
                  onChange={handleSearchChange}
                  onKeypress={handleKeypress}
                />
              </div>
              <div className="search-button-div">
                <Button variant="search" node="Search" className="search-btn" disabled={!searchText.trim()} onClick={handleSearchClicked} />
              </div>
            </div>
          )}
          {isSelectPortView && (
            <div className="verify-all-btn-div">
              <div className="show-verified-div">
                <label>Link View</label>
                <Switch checked={verified} onChange={handlePortViewChange} data-testid="switch" />
                <label>Port View</label>
              </div>
            </div>
          )}
          {rightActionSection}
        </div>
      </div>

      {!isMapview && <div className="sticky-bottom">{/* {table} */}</div>}
      <div className="sticky-bottom">{table}</div>
    </div>
  );
};

export default AdminValidationsLayout;
